/**
 * AuthGuard — Wraps app routes and gates access based on auth state.
 * - Shows a centered spinner while loading
 * - Shows an error alert with retry if initialization fails
 * - Shows OnboardingPage if user has no teams
 * - Renders children (normal routes) otherwise
 * @module components/starnexus/common/AuthGuard
 */

import React from 'react';
import Box from '@amzn/awsui-components-console/box';
import Spinner from '@amzn/awsui-components-console/spinner';
import Alert from '@amzn/awsui-components-console/alert';
import Button from '@amzn/awsui-components-console/button';
import { useAuthStore } from '../../../stores/starnexus/authStore';
import { OnboardingPage } from '../team/OnboardingPage';

export interface AuthGuardProps {
  children: React.ReactNode;
}

export const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const { status, teams, error, initialize } = useAuthStore();

  if (status === 'loading' || status === 'idle') {
    return (
      <Box textAlign="center" padding={{ vertical: 'xxxl' }}>
        <Spinner size="large" />
      </Box>
    );
  }

  if (status === 'error') {
    return (
      <Box padding={{ vertical: 'xxxl', horizontal: 'l' }}>
        <Alert
          type="error"
          header="Failed to load"
          action={
            <Button onClick={() => initialize()}>Retry</Button>
          }
        >
          {error ?? 'An unexpected error occurred while loading your account.'}
        </Alert>
      </Box>
    );
  }

  if (teams.length === 0) {
    return <OnboardingPage />;
  }

  return <>{children}</>;
};

export default AuthGuard;
